import React, { useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Chip,
  Grid,
  Divider,
  Button
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Event as EventIcon, 
  AccessTime as TimeIcon, 
  Person as DoctorIcon, 
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  FilterList as FilterIcon
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 800,
    margin: "0 auto",
  },
  card: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid " + theme.palette.divider,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    transition: "box-shadow 0.2s",
    "&:hover": {
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    }
  },
  statusChip: {
    fontWeight: "bold",
    height: 24,
  },
  doctorInfo: {
      display: "flex",
      alignItems: "center",
      color: theme.palette.text.secondary,
      fontSize: "0.9rem",
      marginTop: 4
  },
  dateHeader: {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
      textTransform: "capitalize"
  },
  emptyState: {
      textAlign: "center",
      padding: theme.spacing(5),
      color: theme.palette.text.secondary
  },
  filterContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(3),
      justifyContent: "center"
  },
  filterButton: {
      flex: "1 1 45%",
      minWidth: 120,
      [theme.breakpoints.up("sm")]: {
          flex: "1 1 auto",
      }
  }
}));

const statusConfig = {
  scheduled: { label: "Programada", color: "#2196f3", icon: <PendingIcon style={{ fontSize: 16 }} /> },
  completed: { label: "Completada", color: "#4caf50", icon: <CheckIcon style={{ fontSize: 16 }} /> },
  cancelled: { label: "Cancelada", color: "#f44336", icon: <CancelIcon style={{ fontSize: 16 }} /> },
};

const HistoryView = ({ data }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState("all");

  const patientName = data.patientName || "Paciente";
  const appointments = data.appointments || [];

  const filteredAppointments = appointments.filter((appt) => {
    if (filter === "all") return true;
    return appt.status === filter;
  });

  const sortedAppointments = [...filteredAppointments].sort((a, b) => {
      return new Date(b.date + "T" + b.startTime) - new Date(a.date + "T" + a.startTime);
  });

  const FilterButton = ({ value, label, icon }) => (
      <Button
          variant={filter === value ? "contained" : "outlined"}
          color="primary"
          onClick={() => setFilter(value)}
          className={classes.filterButton}
          startIcon={icon}
          size="medium"
      >
          {label}
      </Button>
  );

  return (
    <div className={classes.root}>
      <Box mb={3} textAlign="center">
        <Typography variant="h5" style={{ fontWeight: 700 }}>
          Citas de {patientName}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Historial completo de consultas
        </Typography>
      </Box>

      <div className={classes.filterContainer}>
          <FilterButton value="all" label="Todas" icon={<FilterIcon />} />
          <FilterButton value="scheduled" label="Programadas" icon={<PendingIcon />} />
          <FilterButton value="completed" label="Completadas" icon={<CheckIcon />} />
          <FilterButton value="cancelled" label="Canceladas" icon={<CancelIcon />} />
      </div>

      <Box>
        {sortedAppointments.length > 0 ? (
          sortedAppointments.map((appt) => {
            const statusData = statusConfig[appt.status] || { label: appt.status, color: "#9e9e9e" };
            const date = parseISO(appt.date);
            const dateStr = format(date, "EEEE d 'de' MMMM yyyy", { locale: es });
            const timeStr = appt.startTime.slice(0, 5) + " hs";

            return (
              <Paper key={appt.id} className={classes.card} variant="outlined">
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box display="flex" alignItems="center" mb={0.5}>
                        <EventIcon color="action" style={{ marginRight: 8, fontSize: 20 }} />
                        <Typography className={classes.dateHeader}>
                            {dateStr}
                        </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" color="text.secondary" mb={1} ml={3.5}>
                        <TimeIcon style={{ fontSize: 16, marginRight: 4 }} />
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {timeStr}
                        </Typography>
                    </Box>
                    
                    <Divider style={{ margin: "8px 0" }} />

                    <div className={classes.doctorInfo}>
                        <DoctorIcon style={{ marginRight: 8 }} />
                        <div>
                            <Typography variant="body1" style={{ fontWeight: 600, color: "#333" }}>
                                {appt.doctor?.name || "Doctor asignado"}
                            </Typography>
                            {appt.professionalTitle && (
                                <Typography variant="caption" display="block">
                                    {appt.professionalTitle.name}
                                </Typography>
                            )}
                        </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%">
                        <Chip
                            icon={statusData.icon}
                            label={statusData.label}
                            className={classes.statusChip}
                            style={{ 
                                backgroundColor: statusData.color, 
                                color: "#fff"
                            }}
                        />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            );
          })
        ) : (
          <div className={classes.emptyState}>
             <EventIcon style={{ fontSize: 60, opacity: 0.2, marginBottom: 16 }} />
             <Typography variant="h6">No hay citas registradas</Typography>
             <Typography variant="body2">
                No encontramos citas para {patientName} con el filtro seleccionado.
             </Typography>
          </div>
        )}
      </Box>
    </div>
  );
};

export default HistoryView;